import { RedirectMechanismProps } from '@apps/guest/routes/HotelBooking/HotelBooking.types';
import { AccommodationCatalogRegionCircularInput, AccommodationCatalogUnitsOccupancyInput, AccommodationPlacesByCoupleQuery, Maybe } from '@graphql/generated';
import { LatLngLiteral } from '@shared/hooks/useGooglePlaceAPI';

export type Coordinates = {
  latitude: number | null;
  longitude: number | null;
};

export type MapViewPort = Readonly<{ center: google.maps.LatLngLiteral; bounds: google.maps.LatLngBounds }>;

export type AccommodationsConsoleViewMode = 'overlay' | 'standalone';

export interface CircularRegionSearch extends Readonly<AccommodationCatalogRegionCircularInput & { googlePlaceId?: string }> {}

export interface AccommodationsConsoleProps
  extends Readonly<{
    eventHandle?: Maybe<string>;
    eventName: string;
    onClose?: () => void;
    viewMode?: AccommodationsConsoleViewMode;
    selectedHotelId?: string;
    shouldPrioritizeParams?: boolean;
  }> {}

export interface AccommodationsConsoleDialogProps
  extends AccommodationsConsoleProps,
    Readonly<{
      onClose?: () => void;
      isOpen: boolean;
    }> {}

export type AccommodationsConsoleState = Readonly<{
  circularRegionSearch: CircularRegionSearch | null;
  searchLocation?: string;
  searchDates: {
    checkin: string;
    checkout: string;
  };
  occupancies: AccommodationCatalogUnitsOccupancyInput;
  selectedHotelId?: string;

  /**
   * Venue information for the event
   * location: Present when bestLocationAnchor is event_venue or event_location, null otherwise
   */
  venue: Readonly<{
    name: string;
    location: Maybe<LatLngLiteral>;
  }>;
}>;

export type GuestRating = 6 | 7 | 8 | 9;

export enum SortOption {
  COUPLES_PICKS = 'couple-picks',
  TOP_MATCHES = 'top-matches',
  PRICE_LOW_TO_HIGH = 'price-low-to-high',
  PRICE_HIGH_TO_LOW = 'price-high-to-low',
  MOST_SAVINGS = 'most-savings',
  HIGHEST_DISCOUNT = 'highest-discount',
  DISTANCE = 'distance',
}

export type Filters = Readonly<{
  facilities: string[];
  starRating: number[];
  userRating: GuestRating[];
  priceRange: [number, number];
  cug: boolean;
  pickedByCouple: boolean;
  showAvailableOnly: boolean;
  sortBy?: SortOption;
}>;

export interface AccommodationListingDTOBase
  extends Readonly<{
    id: string;
    joyPlaceId?: Maybe<string>;
    name: string;
    userNote?: Maybe<string>;
    isFavorite?: Maybe<boolean>;

    location: {
      lat: number;
      lng: number;
    };

    address: {
      street?: Maybe<string>;
      city?: Maybe<string>;
      country?: Maybe<string>;
    };

    starRating?: Maybe<number>;
    userRating: Maybe<{
      ratingValue?: Maybe<number>;
      ratingCount?: Maybe<number>;
    }>;

    mediaAssets: {
      isLoading: boolean;
      photos: Array<{ url: string }>;
    };

    /**
     * Used for filtering purposes
     */
    facilities: Array<{ label: string }>;

    isPickedByCouple?: boolean;

    /**
     * Ranking score for search results.
     */
    searchScore?: number;
    otaProvider?: Maybe<string>;
  }> {}

export type StandardHotelPricingValues = Readonly<{
  discountPercentage: number | undefined;
  nightlyBaseRateString: string;
  strikeoutNightlyBaseRateString: string | undefined;
  totalRateString: string;
  nightlyBaseRate: number;
}>;

export interface JoyHotelAccommodationListingDTO
  extends AccommodationListingDTOBase,
    Readonly<{
      type: 'standardHotel';
      pricingInfo: {
        isLoading: boolean;

        values: StandardHotelPricingValues | null;
      };
      redirectMechanisms?: RedirectMechanismProps[];
      isSoldOutForSelectedDates: Maybe<boolean>;
    }> {}

export type HotelRoomBlockPricingValues = Readonly<{
  discountPercentage: number | undefined;
  nightlyBaseRateString: string;
  strikeoutNightlyBaseRateString: string | undefined;
  totalRateString?: undefined;
  nightlyBaseRate: number;
}>;
export interface RoomBlockAccommodationListingDTO
  extends AccommodationListingDTOBase,
    Readonly<{
      type: 'roomBlock';
      cutoffDate: string;
      interestedGuestCount: number;
      numberOfRoomsReserved: number;
      pricingInfo: {
        isLoading: boolean;
        values: HotelRoomBlockPricingValues | null;
      };
      redirectMechanisms?: RedirectMechanismProps[];
      bookingUrl?: Maybe<string>;
    }> {}

export type AccommodationListingDTO = JoyHotelAccommodationListingDTO | RoomBlockAccommodationListingDTO;

export type AccommodationPlace = Readonly<NonNullable<AccommodationPlacesByCoupleQuery['eventByName']>['accommodationPlacesV2'][number]>;
export type RoomBlockWithPricePerNight = Readonly<Merge<NonNullable<AccommodationPlace['accommodationRoomBlock']>, { pricePerNight: number }>>;

type TravelMonetaryValue = {
  floatingPointDecimalString: string;
  valueInMinorUnits: number;
  digits?: number;
  currency: string;
};
export type RawJoyHotelPricingInfo = Readonly<{
  nightlyBaseRate: TravelMonetaryValue;
  strikeoutNightlyBaseRate?: Maybe<TravelMonetaryValue>;
  strikeoutTotalRate?: Maybe<TravelMonetaryValue>;
  totalRate: TravelMonetaryValue;
}>;
